import { Stethoscope, Calendar, GraduationCap, DollarSign } from "lucide-react";
import { useNavigate, useSearchParams } from "react-router-dom";
import MobileHeader from "../components/MobileHeader";
import { DoctorDto } from "../web-api-client";
import ErrorRetry from "../components/ui/ErrorRetry";
import { Button } from "../components/ui/button";
import { scrollToTop } from "../utils/scrollToTop";
import { useSessionStorageState } from "../hooks/useSessionStorageState";
import LoadingIndicator from "../components/LoadingIndicator";
import { getDoctorDefaultImg } from "../utils/defaultImage";
import { useIsMobile } from "../hooks/use-mobile";
import DoctorProfileDesktop from "./DoctorProfileDesktop";
import { useDoctorProfile } from "../hooks/useDoctorProfile";
import { useEffect } from "react";
import { useLanguage } from "../i18n/LanguageContext";
import { useBookingTimeGuard } from "../hooks/useBookingTimeGuard";
import BookingTimeoutDialog from "../components/booking/BookingTimeoutDialog";

function DoctorProfile() {
  const navigate = useNavigate();
  const [isMobile] = useIsMobile();
  const { t } = useLanguage();

  const [searchParams] = useSearchParams();
  const [, setSelectedDoctor] = useSessionStorageState("selectedDoctor", null);
  const doctorId = searchParams.get("doctorId") ?? undefined;

  const {
    data: doctor,
    isLoading,
    isError: hasError,
    refetch,
  } = useDoctorProfile(doctorId);

  const {
    checkBookingTime,
    dialogOpen,
    dialogType,
    closeDialog,
  } = useBookingTimeGuard();
  
  const onBack = () => {
    navigate(-1);
  };

  const headerTitle = doctor?.name ?? "";

  const handleDoctorBooked = (doctor?: DoctorDto) => {
    if (!doctor) return;

    if (!checkBookingTime()) {
      return;
    }

    let doctorId = String(doctor.doctorId);

    scrollToTop();

    sessionStorage.clear();

    const params = new URLSearchParams({
      type: "doctor",
      doctorId,
      step: "chooseTime",
    });

    setSelectedDoctor(doctor);

    setTimeout(() => {
      navigate(`/dat-hen?${params.toString()}`);
    }, 0);
  };

  useEffect(() => {
    document.title = headerTitle ?? t("thongtinbacsi");
  }, [doctor, t]);

  return (
    <div
      className={`flex flex-col ${
        isLoading || hasError ? "h-full" : "min-h-screen"
      }`}
    >
      {/* Header */}
      {isLoading ? (
        <div className="fixed inset-0 z-50 touch-none overscroll-contain">
          <div className="absolute inset-0 bg-white/40" aria-hidden="true" />
          <div className="relative w-full h-full flex items-center justify-center">
            <LoadingIndicator />
          </div>
        </div>
      ) : hasError ? (
        <div>
          {isMobile && (
            <MobileHeader title={t("thongtinbacsi")} onBack={onBack} />
          )}
          <div className="h-full p-4 flex items-center justify-center pb-[15px]">
            <ErrorRetry onRetry={refetch} />
          </div>
        </div>
      ) : isMobile ? (
        <div>
          <MobileHeader title={headerTitle} onBack={onBack} />
          <div className="h-full pb-[60px] relative overflow-hidden">

            {/* Doctor Image */}
            <div className="doctor-image-wrapper relative">
              <div
                className="absolute inset-0 z-20 pointer-events-none"
                style={{
                  background: `url(/images/doctor-item-top-wave.svg) center bottom / 100% 100% no-repeat`,
                }}
              />
              <div className="relative items-end flex justify-center z-10 h-[250px] overflow-hidden">
                {doctor?.processedUrl || doctor?.originalUrl ? (
                  <img
                    src={doctor.processedUrl ?? doctor.originalUrl}
                    alt={doctor.name}
                    className="w-auto h-[110%] object-cover"
                  />
                ) : (
                  doctor && (
                    <img
                      src={getDoctorDefaultImg(doctor.gender)}
                      alt="Doctor"
                      className="w-auto h-[200px] object-cover"
                    />
                  )
                )}
              </div>
            </div>

            {/* Content Card */}
            <div className="bg-white px-6 py-8 min-h-[60vh] relative -mt-[1px]">
              <div className="bg-[#f3f2ff] flex items-start gap-4  rounded-xl px-4 py-3 mb-4">
                <div className="w-[40px] h-[40px] shadow-[0_0_15px_5px_rgba(34,197,94,0.5)] shadow-green-100 bg-white rounded-full flex items-center justify-center">
                  <GraduationCap className="w-5 h-5 text-green-500" />
                </div>
                <div className="flex-1">
                  <p className="text-gray-800 font-medium mb-2">
                    {t("chucdanh")}
                  </p>
                  {doctor?.title || doctor?.titleDescription ? (
                    <p>{doctor.titleDescription ?? doctor.formattedTitle}</p>
                  ) : (
                    <p className="text-gray-500">{t("dangcapnhat")}</p>
                  )}
                </div>
              </div>

              {/* Chuyen khoa */}
              <div className="bg-[#fff8f1] flex items-start gap-4 rounded-xl px-4 py-3 mb-4">
                <div className="w-[40px] h-[40px] shadow-[0_0_15px_5px_rgba(34,197,94,0.5)] shadow-green-100 bg-white rounded-full flex items-center justify-center">
                  <Stethoscope className="w-5 h-5 text-green-500" />
                </div>
                <div className="flex-1">
                  <p className="text-gray-800 font-medium mb-2">
                    {t("chuyenkhoa")}
                  </p>
                  {doctor?.specialty?.trim() ? (
                    <p className="text-gray-700">{doctor.specialty}</p>
                  ) : (
                    <p className="text-gray-500">{t("dangcapnhat")}</p>
                  )}
                </div>
              </div>

              {/* Lich kham */}
              <div className="bg-[#f1faff] flex items-start gap-4  rounded-xl px-4 py-3 mb-4">
                <div className="w-[40px] h-[40px] shadow-[0_0_15px_5px_rgba(34,197,94,0.5)] shadow-green-100 bg-white rounded-full flex items-center justify-center">
                  <Calendar className="w-5 h-5 text-green-500" />
                </div>
                <div className="flex-1">
                  <p className="text-gray-800 font-medium mb-2">
                    {t("lichkham")}
                  </p>
                  <span className="text-foreground line-clamp-1">
                    {doctor?.daysThisWeek && doctor.daysThisWeek.length > 0
                      ? doctor.daysThisWeek.join(", ")
                      : t("khongcolichkham")}
                  </span>
                </div>
              </div>

              {/* Chuyen khoa */}
              <div className="bg-[#f5fff8] flex items-start gap-4  rounded-xl px-4 py-3 mb-4">
                <div className="w-[40px] h-[40px] shadow-[0_0_15px_5px_rgba(34,197,94,0.5)] shadow-green-100 bg-white rounded-full flex items-center justify-center">
                  <DollarSign className="w-5 h-5 text-green-500" />
                </div>
                <div className="flex-1">
                  <p className="text-gray-800 font-medium mb-2">
                    {t("giakham")}
                  </p>
                  <span>
                    {(doctor?.consultationFee ?? 1600000).toLocaleString(
                      "vi-VN",
                    )}
                    đ
                  </span>
                </div>
              </div>

              {doctor?.summary?.trim() ? (
                <div className="mt-6">
                  <p className="text-lg md:text-xl font-medium text-green-800">
                    {t("thongtinchung")}
                  </p>
                  <div className="w-20 h-[0.1rem] mt-2 mb-4 bg-green-400"></div>

                  <div className="space-y-1">
                    {doctor.summary
                      .split(/\r?\n/)
                      .map((line) => line.trim())
                      .filter(Boolean)
                      .map((line, idx) => (
                        <div className="flex items-start gap-3 ml-1" key={idx}>
                          <div className="w-[4px] h-[4px] bg-green-500 rounded-full flex-shrink-0 mt-3"></div>
                          <span className="text-gray-700">{line}</span>
                        </div>
                      ))}
                  </div>
                </div>
              ) : doctor?.description && doctor.description.length > 0 ? (
                doctor.description.map((gr, indx) => (
                  <div className="mt-6" key={indx}>
                    <p className="text-lg md:text-xl font-medium text-green-800">
                      {gr.title || t("thongtinchung")}
                    </p>
                    <div className="w-20 h-[0.1rem] mt-2 mb-4 bg-green-400"></div>

                    <div className="space-y-1">
                      {gr.items && gr.items.length > 0 ? (
                        gr.items.map((item, idx) => (
                          <div
                            className="flex items-start gap-3 ml-1"
                            key={idx}
                          >
                            <div className="w-[4px] h-[4px] bg-green-500 rounded-full flex-shrink-0 mt-3"></div>
                            <span className="text-gray-700">{item}</span>
                          </div>
                        ))
                      ) : (
                        <div className="flex items-start gap-3 ml-1">
                          <div className="w-[4px] h-[4px] bg-green-500 rounded-full flex-shrink-0 mt-3"></div>
                          <span className="text-gray-500">
                            {t("dangcapnhat")}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                ))
              ) : (
                <div className="mt-6">
                  <p className="text-lg md:text-xl font-medium text-green-800">
                    {t("thongtinchung")}
                  </p>
                  <div className="w-20 h-[0.1rem] mt-2 mb-4 bg-green-400"></div>

                  <div className="space-y-1">
                    <div className="flex items-start gap-3 ml-1">
                      <div className="w-[4px] h-[4px] bg-green-500 rounded-full flex-shrink-0 mt-3"></div>
                      <span className="text-gray-500">{t("dangcapnhat")}</span>
                    </div>
                  </div>
                </div>
              )}

              <div className="fixed h-[75px] shadow-sm -bottom-1 left-0 right-0 p-4 bg-white border-t border-gray-100">
                <div className="mx-auto max-w-md h-full">
                  <Button
                    className="h-12 text-base w-full py-2 rounded-lg font-medium transition-all duration-200 bg-[linear-gradient(135deg,#2f7d44_0%,#368f4e_52%,#7cc46a_100%)] text-white hover:from-green-700 hover:to-green-500"
                    onClick={() => handleDoctorBooked(doctor)}
                    disabled={doctor?.daysThisWeek?.length === 0}
                  >
                    <Calendar style={{ width: "1.2rem", height: "1.2rem" }} />
                    {doctor?.daysThisWeek && doctor.daysThisWeek?.length > 0
                      ? t("datkham")
                      : t("hetlich")}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      ) : (
        doctor && (
          <DoctorProfileDesktop
            doctor={doctor}
            handleDoctorBooked={handleDoctorBooked}
          />
        )
      )}

      <BookingTimeoutDialog
        isOpen={dialogOpen}
        type={dialogType}
        onClose={closeDialog}
      />
    </div>
  );
}

export default DoctorProfile;
